import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';

import { useKeyHint } from '../../lib/shortcuts/index.js';
import { useDocHistory } from '../../lib/use-doc-history.js';
import { useProjectFiles } from '../../state/project-files.js';
import { useWorkspace } from '../../state/workspace.js';
import { ContextMenu, IconButton, type MenuItem } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { CLIP_LIST_ID } from './BlendTreeEditor.js';
import {
  allMachines,
  allStates,
  allTransitions,
  conditionLabel,
  fullName,
  machineAt,
  motionLabel,
  parseController,
  removeTransition,
  renamePath,
  serializeController,
  stateByFull,
  transitionAt,
  uniqueName,
  type ControllerDoc,
  type Layer,
  type Machine,
  type SubMachine,
  type TransitionLoc,
  type TransitionRef,
} from './controller-doc.js';
import { LayerInspector, StateInspector } from './Inspectors.js';
import { ParametersPanel } from './ParametersPanel.js';
import { PreviewPane, type PreviewTarget } from './PreviewPane.js';
import { enteredState, syncValues, type PreviewValue } from './preview-model.js';
import { StateGraph, type GraphEdge, type GraphNode } from './StateGraph.js';
import { TransitionInspector } from './TransitionInspector.js';
import './animator.css';

type Selection = { kind: 'node'; key: string } | { kind: 'edge'; loc: TransitionLoc } | { kind: 'layer' } | null;

const PREVIEW_OPEN_KEY = 'prism.animator.previewOpen';
const PREVIEW_HEIGHT_KEY = 'prism.animator.previewHeight';
function readPref(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writePref(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
}

const locId = (l: TransitionLoc) => (l.kind === 'state' ? `state:${l.state}:${l.index}` : `${l.kind}:${l.index}`);

/** Node key of a full state / machine path as seen from the machine at `path`. */
function viewKey(full: string, path: readonly string[], machines: Set<string>): string | null {
  if (full === '@any') return '@any';
  const prefix = path.length ? `${path.join('/')}/` : '';
  if (prefix && !full.startsWith(prefix)) return '@up';
  const rest = full.slice(prefix.length);
  if (!rest) return '@up';
  const slash = rest.indexOf('/');
  if (slash < 0) return machines.has(full) ? `m:${full}` : `s:${full}`;
  return `m:${prefix}${rest.slice(0, slash)}`;
}

/** Visual Animator Controller editor for one `*.controller.json` (edits the open buffer, autosaves). */
export function AnimatorEditor({ path, onShowText }: { path: string; onShowText: () => void }) {
  const file = useWorkspace((s) => s.files[path]);
  const setContent = useWorkspace((s) => s.setContent);
  const saveFile = useWorkspace((s) => s.saveFile);
  const entries = useProjectFiles((s) => s.entries);
  const content = file?.content ?? '';
  const parsed = useMemo(() => parseController(content), [content]);
  const doc = parsed.doc;

  const [layerIndex, setLayerIndex] = useState(0);
  const [machinePath, setMachinePath] = useState<string[]>([]);
  const [selection, setSelection] = useState<Selection>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; items: MenuItem[] } | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Live preview: open state, pane height and the scrubbed parameter values
  // (kept apart from the controller's defaults).
  const [previewOpen, setPreviewOpen] = useState(() => readPref(PREVIEW_OPEN_KEY) !== '0');
  const [previewHeight, setPreviewHeight] = useState(() => Number(readPref(PREVIEW_HEIGHT_KEY)) || 330);
  const [previewValues, setPreviewValues] = useState<Record<string, PreviewValue>>({});
  useEffect(() => {
    if (doc) setPreviewValues((v) => syncValues(doc, v));
  }, [doc]);
  const setPreviewValue = useCallback((name: string, value: PreviewValue) => setPreviewValues((v) => ({ ...v, [name]: value })), []);

  const li = doc ? Math.min(layerIndex, Math.max(0, doc.layers.length - 1)) : 0;
  const layer: Layer | undefined = doc?.layers[li];
  const machine: Machine | null = layer ? machineAt(layer, machinePath) : null;

  useEffect(() => {
    if (layer && !machine) setMachinePath([]);
  }, [layer, machine]);

  const commit = useCallback(
    (text: string) => {
      setContent(path, text);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => void saveFile(path), 500);
    },
    [path, setContent, saveFile],
  );

  // Shared undo service: Ctrl+Z / Ctrl+Shift+Z, the Edit menu and the Undo History panel.
  const history = useDocHistory<string>({
    id: `animator:${path}`,
    label: `Animator · ${path.split('/').pop()}`,
    view: 'code',
    read: () => useWorkspace.getState().files[path]?.content ?? '',
    apply: commit,
  });
  const undoKey = useKeyHint('edit.undo');
  const redoKey = useKeyHint('edit.redo');

  /** Applies a change to a copy of the document and writes it back as one undo step. */
  const edit = useCallback(
    (fn: (d: ControllerDoc) => void, label = 'Edit controller', key?: string) => {
      if (!doc) return;
      const next = structuredClone(doc);
      fn(next);
      const text = serializeController(next);
      if (text === content) return;
      history.record(content, label, key);
      commit(text);
    },
    [doc, content, commit, history],
  );

  const editLayer = useCallback(
    (fn: (l: Layer, d: ControllerDoc) => void, label = 'Edit layer', key?: string) => edit((d) => d.layers[li] && fn(d.layers[li]!, d), label, key),
    [edit, li],
  );

  useEffect(() => () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      void saveFile(path);
    }
  }, [path, saveFile]);

  const clipRefs = useMemo(
    () => entries.filter((e) => e.kind === 'file' && /\.glb$/i.test(e.name)).map((e) => `${e.path}#`),
    [entries],
  );

  if (!file) return null;
  if (!doc) {
    return (
      <div className="anim-editor anim-broken">
        <Icon name="warning" size={22} />
        <p>This controller is not valid JSON: {parsed.error}</p>
        <button type="button" className="anim-chip" onClick={onShowText}>
          Fix it as text
        </button>
      </div>
    );
  }

  // ---- graph model -------------------------------------------------------
  const layerName = layer?.name ?? '';
  const positions = doc.editor?.positions?.[layerName] ?? {};
  const machines = new Set(layer ? allMachines(layer) : []);
  const transitions: TransitionRef[] = layer ? allTransitions(layer) : [];
  const defaultName = machine ? (machine.defaultState ?? machine.states[0]?.name ?? machine.subStateMachines?.[0]?.name) : undefined;
  const defaultFull = defaultName !== undefined ? fullName(machinePath, defaultName) : undefined;

  const nodes: GraphNode[] = [];
  const place = (key: string, i: number, fallback: [number, number]) => {
    const p = positions[key];
    return p ? { x: p[0], y: p[1] } : { x: fallback[0] + (i % 3) * 200, y: fallback[1] + Math.floor(i / 3) * 90 };
  };
  if (machine) {
    nodes.push({ key: '@entry', label: 'Entry', kind: 'entry', ...place('@entry', 0, [0, 0]) });
    nodes.push({ key: '@any', label: 'Any State', kind: 'any', ...place('@any', 0, [0, 90]) });
    if (machinePath.length) nodes.push({ key: '@up', label: `↑ ${machinePath.length > 1 ? machinePath[machinePath.length - 2] : 'Base'}`, kind: 'up', ...place('@up', 0, [0, 180]) });
    let i = 0;
    for (const s of machine.states) {
      const full = fullName(machinePath, s.name);
      nodes.push({ key: `s:${full}`, label: s.name, detail: motionLabel(s.motion), kind: 'state', isDefault: full === defaultFull, ...place(`s:${full}`, i++, [240, 0]) });
    }
    for (const m of machine.subStateMachines ?? []) {
      const full = fullName(machinePath, m.name);
      nodes.push({ key: `m:${full}`, label: m.name, detail: `${m.states.length} states`, kind: 'machine', isDefault: full === defaultFull, ...place(`m:${full}`, i++, [240, 0]) });
    }
  }
  const edges: GraphEdge[] = [];
  for (const r of transitions) {
    const a = viewKey(r.from, machinePath, machines);
    const b = viewKey(r.to, machinePath, machines);
    if (!a || !b || a === b || b === '@any') continue;
    const label = (r.t.conditions ?? []).map((c) => conditionLabel(c, doc.parameters)).join(' & ');
    edges.push({ id: locId(r.loc), from: a, to: b, label });
  }
  if (defaultFull !== undefined) edges.push({ id: '@default', from: '@entry', to: machines.has(defaultFull) ? `m:${defaultFull}` : `s:${defaultFull}` });

  // ---- actions -------------------------------------------------------------
  const setPos = (key: string, x: number, y: number) =>
    edit((d) => {
      d.editor ??= {};
      d.editor.positions ??= {};
      (d.editor.positions[layerName] ??= {})[key] = [x, y];
    }, 'Move node', `move:${key}`);

  const targetFull = (key: string) => (key.startsWith('s:') || key.startsWith('m:') ? key.slice(2) : null);

  const connect = (from: string, to: string) => {
    const target = targetFull(to);
    if (!target) return;
    if (from === '@entry') {
      editLayer((l) => {
        const m = machineAt(l, machinePath);
        if (m) m.defaultState = target.split('/').pop()!;
      }, 'Set default state');
      return;
    }
    if (from === '@any') {
      const index = layer?.anyStateTransitions?.length ?? 0;
      editLayer((l) => void (l.anyStateTransitions = [...(l.anyStateTransitions ?? []), { to: target }]), 'Add transition');
      setSelection({ kind: 'edge', loc: { kind: 'any', index } });
      return;
    }
    const src = targetFull(from);
    if (!src || from.startsWith('m:') || !layer) return;
    const index = stateByFull(layer, src)?.transitions?.length ?? 0;
    editLayer((l) => {
      const s = stateByFull(l, src);
      if (s) s.transitions = [...(s.transitions ?? []), { to: target, hasExitTime: true, exitTime: 1 }];
    }, 'Add transition');
    setSelection({ kind: 'edge', loc: { kind: 'state', state: src, index } });
  };

  const addNode = (kind: 'state' | 'machine' | 'tree', at: { x: number; y: number }) => {
    if (!layer || !machine) return;
    const taken = [...machine.states.map((s) => s.name), ...(machine.subStateMachines ?? []).map((m) => m.name)];
    const name = uniqueName(kind === 'machine' ? 'Sub-State Machine' : kind === 'tree' ? 'Blend Tree' : 'New State', taken);
    const full = fullName(machinePath, name);
    edit((d) => {
      const l = d.layers[li]!;
      const m = machineAt(l, machinePath)!;
      if (kind === 'machine') m.subStateMachines = [...(m.subStateMachines ?? []), { name, states: [] } satisfies SubMachine];
      else m.states.push(kind === 'tree' ? { name, motion: { type: '1d', parameter: d.parameters.find((p) => p.type === 'float')?.name ?? '', children: [] } } : { name });
      d.editor ??= {};
      d.editor.positions ??= {};
      (d.editor.positions[l.name] ??= {})[`${kind === 'machine' ? 'm' : 's'}:${full}`] = [Math.round(at.x / 10) * 10, Math.round(at.y / 10) * 10];
    }, kind === 'machine' ? 'Create sub-state machine' : kind === 'tree' ? 'Create blend tree' : 'Create state');
    setSelection({ kind: 'node', key: `${kind === 'machine' ? 'm' : 's'}:${full}` });
  };

  const deleteNode = (key: string) => {
    const full = targetFull(key);
    if (!full) return;
    editLayer((l) => {
      const parentPath = full.split('/').slice(0, -1);
      const leaf = full.split('/').pop()!;
      const m = machineAt(l, parentPath);
      if (!m) return;
      if (key.startsWith('s:')) m.states = m.states.filter((s) => s.name !== leaf);
      else m.subStateMachines = (m.subStateMachines ?? []).filter((s) => s.name !== leaf);
      if (m.defaultState === leaf) delete m.defaultState;
      // Drop transitions into (or out of) the removed node.
      const gone = (n: string | undefined) => n !== undefined && (n === full || n.startsWith(`${full}/`) || (n === leaf && !allStates(l).some((s) => s.full.split('/').pop() === leaf)));
      for (const s of allStates(l)) if (s.state.transitions) s.state.transitions = s.state.transitions.filter((t) => !gone(t.to));
      if (l.transitions) l.transitions = l.transitions.filter((t) => !gone(t.to) && !gone(t.from));
      if (l.anyStateTransitions) l.anyStateTransitions = l.anyStateTransitions.filter((t) => !gone(t.to));
    }, 'Delete node');
    setSelection(null);
  };

  const renameNode = (key: string, name: string) => {
    const full = targetFull(key);
    if (!full || !machine) return;
    const parent = full.split('/').slice(0, -1);
    const newFull = fullName(parent, name);
    if ([...machine.states.map((s) => s.name), ...(machine.subStateMachines ?? []).map((m) => m.name)].includes(name)) return;
    editLayer((l, d) => {
      renamePath(l, full, newFull);
      const m = machineAt(l, parent);
      const node = key.startsWith('s:') ? m?.states.find((s) => s.name === full.split('/').pop()) : m?.subStateMachines?.find((s) => s.name === full.split('/').pop());
      if (node) node.name = name;
      const pos = d.editor?.positions?.[l.name];
      if (pos) {
        for (const k of Object.keys(pos)) {
          const kind = k.slice(0, 2);
          const p = k.slice(2);
          if ((kind === 's:' || kind === 'm:') && (p === full || p.startsWith(`${full}/`))) {
            pos[kind + newFull + p.slice(full.length)] = pos[k]!;
            delete pos[k];
          }
        }
      }
    }, 'Rename node');
    setSelection({ kind: 'node', key: `${key.slice(0, 2)}${newFull}` });
  };

  const openNode = (key: string) => {
    if (key === '@up') setMachinePath(machinePath.slice(0, -1));
    else if (key.startsWith('m:')) setMachinePath(key.slice(2).split('/'));
    setSelection(null);
  };

  const graphMenu = (x: number, y: number, at: { x: number; y: number }, node: string | null) => {
    const items: MenuItem[] = node?.startsWith('s:') || node?.startsWith('m:')
      ? [
          ...(node.startsWith('m:') ? [{ id: 'open', label: 'Open sub-state machine', icon: 'folderOpen' as const, run: () => openNode(node) }] : []),
          {
            id: 'default',
            label: 'Set as default state',
            icon: 'check',
            run: () =>
              editLayer((l) => {
                const m = machineAt(l, machinePath);
                if (m) m.defaultState = node.slice(2).split('/').pop()!;
              }, 'Set default state'),
          },
          { id: 'sep', label: '', separator: true },
          { id: 'delete', label: 'Delete', icon: 'trash', danger: true, run: () => deleteNode(node) },
        ]
      : [
          { id: 'state', label: 'Create state', icon: 'plus', run: () => addNode('state', at) },
          { id: 'tree', label: 'Create state with blend tree', icon: 'layers', run: () => addNode('tree', at) },
          { id: 'machine', label: 'Create sub-state machine', icon: 'folder', run: () => addNode('machine', at) },
        ];
    setMenu({ x, y, items });
  };

  // ---- inspector -----------------------------------------------------------
  let inspector: ReactNode = <p className="muted anim-hint">Select a state or transition. Right-click the graph to add states.</p>;
  if (layer && selection?.kind === 'layer') {
    inspector = (
      <LayerInspector
        doc={doc}
        layer={layer}
        index={li}
        mutate={(fn) => editLayer((l) => fn(l), 'Edit layer', `layer:${li}`)}
        onRename={(name) => {
          if (doc.layers.some((l) => l.name === name)) return;
          edit((d) => {
            const old = d.layers[li]!.name;
            d.layers[li]!.name = name;
            for (const l of d.layers) if (l.syncedLayer === old) l.syncedLayer = name;
            const pos = d.editor?.positions;
            if (pos?.[old]) {
              pos[name] = pos[old]!;
              delete pos[old];
            }
          }, 'Rename layer');
        }}
        onDelete={() => {
          edit((d) => void d.layers.splice(li, 1), 'Delete layer');
          setLayerIndex(Math.max(0, li - 1));
          setSelection(null);
        }}
      />
    );
  } else if (layer && selection?.kind === 'edge') {
    const t = transitionAt(layer, selection.loc);
    const ref = transitions.find((r) => locId(r.loc) === locId(selection.loc));
    if (t && ref) {
      inspector = (
        <TransitionInspector
          doc={doc}
          layer={layer}
          from={ref.from}
          t={t}
          isAny={ref.from === '@any'}
          mutate={(fn) =>
            editLayer((l) => {
              const x = transitionAt(l, selection.loc);
              if (x) fn(x);
            }, 'Edit transition', `transition:${locId(selection.loc)}`)
          }
          onDelete={() => {
            editLayer((l) => removeTransition(l, selection.loc), 'Delete transition');
            setSelection(null);
          }}
        />
      );
    }
  } else if (layer && selection?.kind === 'node' && selection.key.startsWith('s:')) {
    const full = selection.key.slice(2);
    const state = stateByFull(layer, full);
    if (state) {
      inspector = (
        <StateInspector
          key={full}
          doc={doc}
          state={state}
          full={full}
          isDefault={full === defaultFull}
          outgoing={transitions.filter((r) => r.from === full)}
          mutate={(fn) =>
            editLayer((l) => {
              const s = stateByFull(l, full);
              if (s) fn(s);
            }, 'Edit state', `state:${full}`)
          }
          onRename={(name) => renameNode(selection.key, name)}
          onSetDefault={() =>
            editLayer((l) => {
              const m = machineAt(l, full.split('/').slice(0, -1));
              if (m) m.defaultState = full.split('/').pop()!;
            }, 'Set default state')
          }
          onDelete={() => deleteNode(selection.key)}
          onSelectTransition={(r) => setSelection({ kind: 'edge', loc: r.loc })}
        />
      );
    }
  } else if (layer && selection?.kind === 'node' && selection.key.startsWith('m:')) {
    const full = selection.key.slice(2);
    inspector = (
      <div className="anim-inspector-body">
        <div className="anim-inspector-title">
          <span>Sub-state machine</span>
          <span className="mono">{full}</span>
          <IconButton icon="trash" title="Delete sub-state machine" onClick={() => deleteNode(selection.key)} />
        </div>
        <p className="muted anim-hint">Double-click to open it. Transitions into it enter its default state; its states are addressed as "{full}/State".</p>
        <label className="anim-rename">
          <span>Name</span>
          <input
            className="textfield"
            defaultValue={full.split('/').pop()}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && !v.includes('/') && v !== full.split('/').pop()) renameNode(selection.key, v);
            }}
          />
        </label>
        <button type="button" className="anim-chip" onClick={() => openNode(selection.key)}>
          Open
        </button>
      </div>
    );
  } else if (selection?.kind === 'node' && selection.key === '@any') {
    inspector = <p className="muted anim-hint">Any State: drag from its handle to a state for a transition that can fire from every state of this layer.</p>;
  } else if (selection?.kind === 'node' && selection.key === '@entry') {
    inspector = <p className="muted anim-hint">Entry: drag from its handle to a state to make it this machine's default state.</p>;
  }

  // ---- preview target ------------------------------------------------------
  let previewTarget: PreviewTarget | null = null;
  if (layer) {
    // Synced layers play their source layer's states.
    const states = (layer.syncedLayer && doc.layers.find((l) => l.name === layer.syncedLayer)) || layer;
    const enter = (full: string) => enteredState(states, full);
    if (selection?.kind === 'edge') {
      const ref = transitions.find((r) => locId(r.loc) === locId(selection.loc));
      const from = ref && enter(ref.from);
      const to = ref && enter(ref.to);
      if (ref && from && to) previewTarget = { kind: 'transition', layer: li, from, to, t: ref.t };
    } else {
      const key = selection?.kind === 'node' ? selection.key : '';
      const state = enter(key.startsWith('s:') || key.startsWith('m:') ? key.slice(2) : machinePath.join('/'));
      if (state) previewTarget = { kind: 'state', layer: li, state };
    }
  }

  const resizePreview = (e: ReactPointerEvent<HTMLDivElement>) => {
    const startY = e.clientY;
    const start = previewHeight;
    const move = (ev: PointerEvent) => setPreviewHeight(Math.max(160, Math.min(900, start + startY - ev.clientY)));
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      writePref(PREVIEW_HEIGHT_KEY, String(Math.max(160, Math.min(900, start + startY - ev.clientY))));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div className="anim-editor">
      <datalist id={CLIP_LIST_ID}>
        {clipRefs.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <div className="anim-toolbar">
        <div className="anim-layers" role="tablist">
          {doc.layers.map((l, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === li}
              className={`anim-layer-tab${i === li ? ' is-active' : ''}`}
              onClick={() => {
                setLayerIndex(i);
                setMachinePath([]);
                setSelection(i === li ? { kind: 'layer' } : null);
              }}
              onDoubleClick={() => setSelection({ kind: 'layer' })}
              title="Click again for layer settings"
            >
              {l.name || `Layer ${i}`}
              {l.blending === 'additive' && <span className="anim-badge">add</span>}
              {l.syncedLayer && <span className="anim-badge">sync</span>}
            </button>
          ))}
          <IconButton
            icon="plus"
            title="Add layer"
            onClick={() => {
              edit((d) => void d.layers.push({ name: uniqueName('New Layer', d.layers.map((l) => l.name)), weight: 1, states: [] }), 'Add layer');
              setLayerIndex(doc.layers.length);
              setMachinePath([]);
              setSelection({ kind: 'layer' });
            }}
          />
        </div>
        <nav className="anim-crumbs">
          <button type="button" onClick={() => setMachinePath([])}>
            {layerName}
          </button>
          {machinePath.map((m, i) => (
            <span key={i}>
              <Icon name="chevronRight" size={11} />
              <button type="button" onClick={() => setMachinePath(machinePath.slice(0, i + 1))}>
                {m}
              </button>
            </span>
          ))}
        </nav>
        <div className="anim-toolbar-actions">
          <IconButton icon="plus" title="Add state" onClick={() => addNode('state', { x: 240, y: 20 + (machine?.states.length ?? 0) * 60 })} />
          <IconButton icon="layers" title="Layer settings" active={selection?.kind === 'layer'} onClick={() => setSelection({ kind: 'layer' })} />
          <IconButton icon="undo" title={history.undoLabel ? `Undo ${history.undoLabel}` : 'Undo'} shortcut={undoKey} disabled={!history.canUndo} onClick={history.undo} />
          <IconButton icon="redo" title={history.redoLabel ? `Redo ${history.redoLabel}` : 'Redo'} shortcut={redoKey} disabled={!history.canRedo} onClick={history.redo} />
          <IconButton
            icon="eye"
            title={previewOpen ? 'Hide preview' : 'Show preview'}
            active={previewOpen}
            onClick={() => {
              writePref(PREVIEW_OPEN_KEY, previewOpen ? '0' : '1');
              setPreviewOpen(!previewOpen);
            }}
          />
          <IconButton icon="code" title="Edit as text" onClick={onShowText} />
        </div>
      </div>
      <div className="anim-body">
        <aside className="anim-left">
          <ParametersPanel
            doc={doc}
            edit={(fn) => edit(fn, 'Edit parameters', 'parameters')}
            preview={previewOpen ? { values: previewValues, set: setPreviewValue } : undefined}
          />
        </aside>
        <main className="anim-center">
          {layer?.syncedLayer ? (
            <div className="anim-empty">
              <p>
                This layer is synced to <strong>{layer.syncedLayer}</strong> and plays its states. Edit motion overrides in the layer settings.
              </p>
            </div>
          ) : (
            <StateGraph
              nodes={nodes}
              edges={edges}
              selectedNode={selection?.kind === 'node' ? selection.key : null}
              selectedEdge={selection?.kind === 'edge' ? locId(selection.loc) : null}
              onSelectNode={(key) => setSelection({ kind: 'node', key })}
              onSelectEdge={(id) => {
                const r = transitions.find((t) => locId(t.loc) === id);
                if (r) setSelection({ kind: 'edge', loc: r.loc });
              }}
              onMoveNode={setPos}
              onConnect={connect}
              onOpen={openNode}
              onBackground={() => setSelection(null)}
              onContextMenu={graphMenu}
            />
          )}
        </main>
        <aside className={`anim-right${previewOpen ? ' has-preview' : ''}`}>
          <div className="anim-right-inspector">{inspector}</div>
          {previewOpen && (
            <>
              <div className="anim-preview-split" role="separator" aria-orientation="horizontal" onPointerDown={resizePreview} />
              <div className="anim-preview-slot" style={{ height: previewHeight }}>
                <PreviewPane path={path} content={content} doc={doc} target={previewTarget} values={previewValues} onValues={setPreviewValues} />
              </div>
            </>
          )}
        </aside>
      </div>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menu.items} onClose={() => setMenu(null)} />}
    </div>
  );
}
